import { useEffect, useRef, useState } from "react";
import api, { absoluteUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Building2, Upload, Trash2, Save, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function OrganisationSettings() {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    company_name: "", abn: "", address: "", contact_email: "", contact_phone: "",
    document_header_text: "", document_footer_text: "",
  });
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const reload = () => api.get("/organisation/settings").then(r => {
    setSettings(r.data);
    setForm({
      company_name: r.data.company_name || "",
      abn: r.data.abn || "",
      address: r.data.address || "",
      contact_email: r.data.contact_email || "",
      contact_phone: r.data.contact_phone || "",
      document_header_text: r.data.document_header_text || "",
      document_footer_text: r.data.document_footer_text || "",
    });
  });
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/organisation/settings", form);
      toast.success("Organisation settings saved.");
      reload();
      // Trigger event so navbar branding re-fetches if it ever subscribes
      window.dispatchEvent(new Event("flowqa:org-branding-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const ok = ["png", "jpg", "jpeg", "svg"].includes(file.name.toLowerCase().split(".").pop());
    if (!ok) { toast.error("Logo must be PNG, JPG, JPEG or SVG"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/organisation/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded.");
      reload();
      window.dispatchEvent(new Event("flowqa:org-branding-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove the organisation logo?")) return;
    setBusy(true);
    try {
      await api.delete("/organisation/logo");
      toast.success("Logo removed.");
      reload();
      window.dispatchEvent(new Event("flowqa:org-branding-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 md:p-8" data-testid="org-settings-page">
      <div className="overline text-teal-700">Branding</div>
      <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
        Organisation Settings
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-slate-600">
        Customer branding for FlowQA. Logo appears on client-facing pages and generated documents.
        FlowQA branding stays in the in-app navigation.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="border-slate-200 p-5 lg:col-span-1" data-testid="logo-card">
          <div className="overline">Company Logo</div>
          <div className="mt-2 flex aspect-video items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50" data-testid="logo-preview">
            {settings.logo_file_url ? (
              <img
                src={absoluteUrl(settings.logo_file_url)}
                alt={settings.company_name || "Company logo"}
                className="max-h-32 max-w-full object-contain"
                data-testid="logo-preview-img"
              />
            ) : (
              <div className="flex flex-col items-center text-slate-400">
                <Building2 className="size-10"/>
                <div className="mt-1 text-xs">No logo uploaded</div>
              </div>
            )}
          </div>
          {settings.logo_original_filename && (
            <div className="mt-2 truncate text-[11px] text-slate-500" title={settings.logo_original_filename}>
              {settings.logo_original_filename}
            </div>
          )}
          {isAdmin && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                className="hidden"
                data-testid="logo-file-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="upload-logo-btn"
              >
                <Upload className="mr-2 size-4"/>
                {settings.logo_file_url ? "Replace Logo" : "Upload Logo"}
              </Button>
              {settings.logo_file_url && (
                <Button
                  variant="outline"
                  onClick={removeLogo}
                  disabled={busy}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="remove-logo-btn"
                >
                  <Trash2 className="mr-2 size-4"/>Remove
                </Button>
              )}
            </div>
          )}
          {!isAdmin && (
            <div className="mt-3 text-xs text-slate-500">Only Admin can change the company logo.</div>
          )}
          <p className="mt-3 text-[11px] text-slate-500">PNG, JPG or SVG · max 5 MB.</p>
        </Card>

        <Card className="border-slate-200 p-5 lg:col-span-2" data-testid="org-form-card">
          <div className="overline">Company Details</div>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label className="text-xs">Company Name *</Label>
              <Input
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                disabled={!isAdmin}
                data-testid="org-company-name"
              />
            </div>
            <div>
              <Label className="text-xs">ABN</Label>
              <Input value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} disabled={!isAdmin} data-testid="org-abn"/>
            </div>
            <div>
              <Label className="text-xs">Contact Phone</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} disabled={!isAdmin} data-testid="org-phone"/>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Contact Email</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                disabled={!isAdmin}
                data-testid="org-email"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} disabled={!isAdmin} data-testid="org-address"/>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Default Document Header Text</Label>
              <Textarea
                rows={2}
                value={form.document_header_text}
                onChange={(e) => setForm({ ...form, document_header_text: e.target.value })}
                disabled={!isAdmin}
                placeholder="Appears on generated reports / ITPs"
                data-testid="org-header-text"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Default Document Footer Text</Label>
              <Textarea
                rows={2}
                value={form.document_footer_text}
                onChange={(e) => setForm({ ...form, document_footer_text: e.target.value })}
                disabled={!isAdmin}
                placeholder="Appears in footer of generated documents"
                data-testid="org-footer-text"
              />
            </div>
          </div>
          {isAdmin && (
            <div className="mt-4 flex justify-end">
              <Button onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800" data-testid="save-org-settings">
                {busy ? <><Loader2 className="mr-2 size-4 animate-spin"/>Saving…</> : <><Save className="mr-2 size-4"/>Save Settings</>}
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
